import React, { Suspense, lazy } from "react";
import { HelmetProvider } from "react-helmet-async";
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ClerkProvider, RedirectToSignIn, useAuth } from "@clerk/react";

const Index = lazy(() => import("./pages/Index"));
const Ebooks = lazy(() => import("@/pages/Ebooks"));
const EbookSuccess = lazy(() => import("@/pages/EbookSuccess"));
const LoginPage = lazy(() => import("@/pages/Login"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));

const queryClient = new QueryClient();
const clerkKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string;

function ProtectedRoute({ children, redirectUrl }: { children: React.ReactNode; redirectUrl?: string }) {
  const { isSignedIn, isLoaded } = useAuth();
  if (!isLoaded) return null;
  if (!isSignedIn) return <RedirectToSignIn redirectUrl={redirectUrl ?? "/dashboard"} />;
  return <>{children}</>;
}

const App = () => (
  <HelmetProvider>
  <ClerkProvider publishableKey={clerkKey} signInUrl="/login">
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Analytics />
        <SpeedInsights />
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Suspense fallback={<div className="flex items-center justify-center min-h-screen">Loading…</div>}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/ebooks" element={<ProtectedRoute redirectUrl="/ebooks"><Ebooks /></ProtectedRoute>} />
              <Route path="/ebooks/success" element={<ProtectedRoute redirectUrl="/ebooks/success"><EbookSuccess /></ProtectedRoute>} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ClerkProvider>
  </HelmetProvider>
);

export default App;
